<template>
  <div class="content">
    <div class="popup-page">
      <card class="popup-toolbar">
        <div class="popup-toolbar-row">
          <div class="popup-filter-field">
            <span class="popup-filter-label">표시</span>
            <el-select @change="handleStateChange" class="select-primary" size="large" placeholder="선택" v-model="state.state">
              <el-option v-for="option in state.options" class="select-primary" :value="option.value" :label="option.label" :key="option.label" />
            </el-select>
          </div>

          <div class="popup-filter-field popup-type-filter">
            <span class="popup-filter-label">팝업 유형</span>
            <div class="popup-radio-group">
              <base-radio name="all" inline v-model="popupTypeFilter">전체</base-radio>
              <base-radio name="bottomPopup" inline v-model="popupTypeFilter">하단 팝업</base-radio>
              <base-radio name="imageModal" inline v-model="popupTypeFilter">이미지 모달</base-radio>
            </div>
          </div>

          <div class="popup-toolbar-actions">
            <base-button @click="goPopupForm()" type="default">팝업 등록</base-button>
            <base-button @click="setOrder()" type="default">순서 저장</base-button>
          </div>
        </div>
      </card>

      <div class="popup-list-header">
        <div class="popup-list-heading">
          <h5>목록</h5>
          <span class="popup-list-count">{{ filteredPopups.length }}개</span>
          <span v-if="selectedArtistName" class="popup-list-current">{{ selectedArtistName }}</span>
        </div>
        <div class="popup-artist-filter">
          <span class="popup-filter-label">아티스트</span>
          <div class="popup-tabs" role="tablist" aria-label="아티스트별 팝업">
            <button type="button" class="popup-tab" :class="{ active: selectedUnitId === null }" @click="selectedUnitId = null">전체</button>
            <button
              v-for="unit in artistTabs"
              :key="unit.unitId"
              type="button"
              class="popup-tab"
              :class="{ active: selectedUnitId === unit.unitId }"
              @click="selectedUnitId = unit.unitId"
            >
              {{ unit.name }}
            </button>
          </div>
        </div>
      </div>

      <draggable class="row popup-grid" handle=".popup-drag-handle" v-model="filteredPopups" :animation="150" @start="onDragStart" @end="onDragEnd">
        <div
          class="col-xl-3 col-lg-4 col-md-6 col-sm-6 popup-card-column"
          v-for="item in filteredPopups"
          :key="item.id"
          role="button"
          tabindex="0"
          @click="openPopup(item.id)"
          @keydown.enter="openPopup(item.id)"
        >
          <card class="popup-card">
            <div slot="image" class="popup-image-frame">
              <img v-if="item.imageFileUrl" class="popup-image" :src="item.imageFileUrl" :alt="item.name" />
              <img v-else class="popup-image popup-image-placeholder" src="@/assets/img/blip-logo.png" alt="이미지 없음" />
              <span class="popup-order">#{{ Number(item.visibleOrder) + 1 }}</span>
              <button type="button" class="popup-drag-handle" title="드래그하여 순서 변경" aria-label="드래그하여 순서 변경" @click.stop>
                <i class="tim-icons icon-bullet-list-67"></i>
              </button>
            </div>

            <div class="popup-card-content">
              <div class="popup-card-heading">
                <div class="popup-card-title-wrap">
                  <span class="popup-card-id">ID {{ item.id }}</span>
                  <h5 class="popup-card-title" :title="item.name">{{ item.name }}</h5>
                </div>
                <span class="badge badge-pill" :class="popupState(item).badgeClass">{{ popupState(item).label }}</span>
              </div>

              <div class="popup-card-badges">
                <span class="badge badge-pill badge-info">{{ item.isAd ? "하단 팝업" : "이미지 모달" }}</span>
                <span class="badge badge-pill badge-light">{{ item.linkType === "EXTERNAL" ? "외부 이동" : "내부 이동" }}</span>
              </div>

              <dl class="popup-meta-list">
                <div class="popup-meta-row">
                  <dt>아티스트</dt>
                  <dd :title="artistNames(item).join(', ')">{{ targetArtistSummary(item) }}</dd>
                </div>
                <div class="popup-meta-row">
                  <dt>국가</dt>
                  <dd :title="countryNames(item).join(', ')">{{ targetCountrySummary(item) }}</dd>
                </div>
                <div class="popup-meta-row">
                  <dt>노출 기간</dt>
                  <dd class="popup-period">
                    <span>{{ item.startTimeLabel }}</span>
                    <span>{{ item.endTimeLabel }}</span>
                  </dd>
                </div>
              </dl>

              <div class="popup-link-summary">
                <div class="popup-link-row">
                  <strong class="popup-platform">앱</strong>
                  <span class="popup-link-value" :title="item.link || ''">{{ item.link || "-" }}</span>
                  <span class="badge badge-pill" :class="hasLink(item.link) ? 'badge-success' : 'badge-secondary'">
                    {{ hasLink(item.link) ? "노출" : "미노출" }}
                  </span>
                </div>
                <div class="popup-link-row">
                  <strong class="popup-platform">웹</strong>
                  <span class="popup-link-value" :title="item.webLink || ''">{{ item.webLink || "-" }}</span>
                  <span class="badge badge-pill" :class="hasLink(item.webLink) ? 'badge-success' : 'badge-secondary'">
                    {{ hasLink(item.webLink) ? "노출" : "미노출" }}
                  </span>
                </div>
              </div>
            </div>
          </card>
        </div>
      </draggable>

      <div v-if="filteredPopups.length === 0" class="popup-empty">조건에 맞는 팝업이 없습니다.</div>
    </div>
  </div>
</template>
<script>
import { BaseButton, BaseRadio, Card } from "@/components/index";
import { formatDateTime } from "../../util/DateUtils";
import draggable from "vuedraggable";
import { Toast } from "@/util/Toast";

export default {
  components: {
    draggable,
    Card,
    BaseButton,
    BaseRadio,
  },
  async created() {
    await Promise.all([this.getAllUnits(), this.getCountryCodes(), this.getPopups()]);
  },
  data() {
    return {
      state: {
        state: 1,
        options: [
          { label: "전체", value: null },
          { label: "표시", value: 1 },
          { label: "숨김", value: 0 },
        ],
      },
      popups: [],
      units: [],
      countries: [],
      selectedUnitId: null,
      popupTypeFilter: "all",
      dragging: false,
    };
  },
  computed: {
    artistTabs() {
      const assignedIds = [];
      this.popups.forEach((popup) => {
        popup.unitIdList.forEach((unitId) => {
          if (!assignedIds.includes(unitId)) {
            assignedIds.push(unitId);
          }
        });
      });
      return assignedIds
        .map((unitId) => {
          const unit = this.units.find((candidate) => candidate.unitId === unitId);
          return { unitId, name: unit?.blipName || unit?.name || `아티스트 #${unitId}` };
        })
        .sort((a, b) => {
          const aIndex = this.units.findIndex((unit) => unit.unitId === a.unitId);
          const bIndex = this.units.findIndex((unit) => unit.unitId === b.unitId);
          return (aIndex < 0 ? this.units.length : aIndex) - (bIndex < 0 ? this.units.length : bIndex);
        });
    },
    selectedArtistName() {
      if (this.selectedUnitId === null) {
        return null;
      }
      return this.artistTabs.find((unit) => unit.unitId === this.selectedUnitId)?.name || null;
    },
    filteredPopups: {
      get() {
        return this.popups.filter((popup) => {
          const matchesArtist = this.selectedUnitId === null || popup.unitIdList.includes(this.selectedUnitId);
          const matchesType =
            this.popupTypeFilter === "all" ||
            (this.popupTypeFilter === "bottomPopup" && Boolean(popup.isAd)) ||
            (this.popupTypeFilter === "imageModal" && !popup.isAd);
          return matchesArtist && matchesType;
        });
      },
      set(reorderedPopups) {
        const visibleIds = this.filteredPopups.map((popup) => popup.id);
        let visibleIndex = 0;
        this.popups = this.popups.map((popup) => (visibleIds.includes(popup.id) ? reorderedPopups[visibleIndex++] : popup));
      },
    },
  },
  methods: {
    hasLink(link) {
      return Boolean(link && link.trim());
    },
    parseJsonSafely(input) {
      if (!input || typeof input !== "string") {
        return input;
      }
      try {
        return JSON.parse(input);
      } catch (e) {
        return null;
      }
    },
    parseCsv(input, parser = (value) => value) {
      if (Array.isArray(input)) {
        return input.map(parser).filter((value) => value !== null && value !== undefined && value !== "");
      }
      if (!input) {
        return [];
      }
      return String(input)
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
        .map(parser)
        .filter((value) => value !== null && value !== undefined && !Number.isNaN(value));
    },
    async getAllUnits() {
      const res = await this.$axios.get(`/artistUnit`);
      this.units = res.data.data || [];
    },
    async getCountryCodes() {
      const res = await this.$axios.get(`/common/countryCodes`);
      this.countries = res.data.data || [];
    },
    async handleStateChange() {
      await this.getPopups();
      if (this.selectedUnitId !== null && !this.artistTabs.some((unit) => unit.unitId === this.selectedUnitId)) {
        this.selectedUnitId = null;
      }
    },
    async getPopups() {
      return this.$axios.get(`/popup`, { params: { state: this.state.state } }).then((res) => {
        this.popups = (res.data.data || []).map((popup) => {
          const images = this.parseJsonSafely(popup.images) || [];
          const koImage = images.find((image) => image.code === "ko") || images[0];
          return {
            ...popup,
            images,
            imageFileUrl: koImage?.imageFileId ? window.Endpoints.imageServerUrl + koImage.imageFileId : null,
            startTimeLabel: formatDateTime(popup.startTime),
            endTimeLabel: formatDateTime(popup.endTime),
            unitIdList: this.parseCsv(popup.unitIds, Number),
            countryCodeList: this.parseCsv(popup.countryCodes),
          };
        });
      });
    },
    popupState(popup) {
      if (popup.state !== 1) {
        return { label: "숨김", badgeClass: "badge-secondary" };
      }
      const now = Date.now();
      const startsAt = new Date(popup.startTime).getTime();
      const endsAt = new Date(popup.endTime).getTime();
      if (now < startsAt || now > endsAt) {
        return { label: "기간 외", badgeClass: "badge-warning" };
      }
      return { label: "표시", badgeClass: "badge-success" };
    },
    artistNames(popup) {
      return popup.unitIdList.map((unitId) => {
        const unit = this.units.find((candidate) => candidate.unitId === unitId);
        return unit?.blipName || unit?.name || `#${unitId}`;
      });
    },
    countryNames(popup) {
      return popup.countryCodeList.map((countryCode) => {
        const country = this.countries.find((candidate) => (candidate.isoCode || candidate.code) === countryCode);
        return country?.name || countryCode;
      });
    },
    formatTargetSummary(names, emptyLabel) {
      if (names.length === 0) {
        return emptyLabel;
      }
      const visibleNames = names.slice(0, 2);
      return names.length > visibleNames.length ? `${visibleNames.join(", ")} 외 ${names.length - visibleNames.length}개` : visibleNames.join(", ");
    },
    targetArtistSummary(popup) {
      return this.formatTargetSummary(this.artistNames(popup), "전체 아티스트");
    },
    targetCountrySummary(popup) {
      return this.formatTargetSummary(this.countryNames(popup), "전체 국가");
    },
    onDragStart() {
      this.dragging = true;
    },
    onDragEnd() {
      setTimeout(() => {
        this.dragging = false;
      }, 0);
    },
    openPopup(popupId) {
      if (!this.dragging) {
        this.goPopupForm(popupId);
      }
    },
    async setOrder() {
      await this.$axios.put(`/popup/order`, { params: { ids: this.popups.map((popup) => popup.id) } });
      this.popups = this.popups.map((popup, index) => ({ ...popup, visibleOrder: index }));
      Toast.success("순서가 저장되었습니다");
    },
    goPopupForm(popupId) {
      if (popupId) {
        this.$router.push(`/banner/popupForm?popupId=${popupId}`);
      } else {
        this.$router.push(`/banner/popupForm`);
      }
    },
  },
};
</script>
<style scoped lang="scss">
.popup-page {
  width: 100%;
  max-width: 1680px;
  margin: 0 auto;
}

.popup-toolbar-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 32px;
  align-items: flex-end;
}

.popup-filter-field {
  display: flex;
  min-width: 180px;
  flex-direction: column;
  gap: 8px;
}

.popup-filter-label {
  color: #525f7f;
  font-size: 0.78rem;
  font-weight: 700;
}

.popup-type-filter {
  flex: 1 1 360px;
}

.popup-radio-group {
  display: flex;
  min-height: 40px;
  flex-wrap: wrap;
  align-items: center;
}

.popup-toolbar-actions {
  display: flex;
  margin-left: auto;
  gap: 8px;
}

.popup-list-header {
  margin: 26px 0 14px;
  padding: 0 4px;
}

.popup-list-heading {
  display: flex;
  min-height: 28px;
  gap: 8px;
  align-items: center;
}

.popup-list-heading h5 {
  margin: 0;
  color: #32325d;
  font-size: 1rem;
  font-weight: 700;
}

.popup-list-count,
.popup-list-current {
  color: #8898aa;
  font-size: 0.78rem;
  font-weight: 600;
}

.popup-list-current::before {
  margin-right: 8px;
  color: #d9dde7;
  content: "|";
}

.popup-artist-filter {
  display: flex;
  margin-top: 10px;
  gap: 12px;
  align-items: center;
}

.popup-tabs {
  display: flex;
  min-width: 0;
  overflow-x: auto;
  gap: 6px;
  scrollbar-width: thin;
}

.popup-tab {
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid #d9dde7;
  border-radius: 4px;
  background: #fff;
  color: #525f7f;
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.popup-tab:hover,
.popup-tab.active {
  border-color: #00bf9a;
  background: #00bf9a;
  color: #fff;
}

.popup-grid {
  align-items: stretch;
}

.popup-card-column {
  display: flex;
  margin-bottom: 24px;
  outline: none;
}

.popup-card-column:focus .popup-card,
.popup-card-column:hover .popup-card {
  border-color: rgba(0, 191, 154, 0.45);
  box-shadow: 0 8px 24px rgba(34, 42, 66, 0.12);
  transform: translateY(-2px);
}

.popup-card {
  width: 100%;
  height: 100%;
  border: 1px solid transparent;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.popup-image-frame {
  position: relative;
  display: flex;
  width: 100%;
  aspect-ratio: 4 / 3;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f4f5f7;
}

.popup-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.popup-image-placeholder {
  padding: 20%;
}

.popup-order,
.popup-drag-handle {
  position: absolute;
  top: 10px;
  display: inline-flex;
  min-width: 30px;
  height: 30px;
  border-radius: 4px;
  background: rgba(34, 42, 66, 0.82);
  color: #fff;
  line-height: 1;
  align-items: center;
  justify-content: center;
}

.popup-order {
  left: 10px;
  padding: 0 8px;
  font-size: 0.75rem;
  font-weight: 700;
}

.popup-drag-handle {
  right: 10px;
  width: 30px;
  padding: 0;
  border: 0;
  cursor: grab;
}

.popup-drag-handle i,
.popup-drag-handle i::before {
  display: block;
  margin: 0;
  font-size: 0.88rem;
  line-height: 1;
}

.popup-drag-handle:active {
  cursor: grabbing;
}

.popup-card-content {
  display: flex;
  min-height: 330px;
  flex-direction: column;
}

.popup-card-heading {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
}

.popup-card-title-wrap {
  min-width: 0;
}

.popup-card-id {
  display: block;
  margin-bottom: 3px;
  color: #8898aa;
  font-size: 0.7rem;
}

.popup-card-title {
  overflow: hidden;
  margin: 0;
  color: #32325d;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.popup-card-badges {
  display: flex;
  margin-top: 12px;
  flex-wrap: wrap;
  gap: 6px;
}

.popup-meta-list {
  margin: 14px 0 0;
}

.popup-meta-row {
  display: grid;
  grid-template-columns: 66px minmax(0, 1fr);
  gap: 8px;
  margin-bottom: 7px;
  font-size: 0.76rem;
  line-height: 1.45;
}

.popup-meta-row dt {
  color: #8898aa;
  font-weight: 600;
}

.popup-meta-row dd {
  min-width: 0;
  overflow: hidden;
  margin: 0;
  color: #525f7f;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.popup-period {
  display: flex;
  flex-direction: column;
}

.popup-link-summary {
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid rgba(34, 42, 66, 0.12);
}

.popup-link-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-height: 28px;
}

.popup-link-row + .popup-link-row {
  margin-top: 4px;
}

.popup-platform {
  font-size: 0.8rem;
}

.popup-link-value {
  min-width: 0;
  overflow: hidden;
  color: #6c757d;
  font-size: 0.72rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge {
  white-space: nowrap;
}

.badge-light {
  border: 1px solid #d9dde7;
  background: #fff;
  color: #525f7f;
}

.popup-empty {
  padding: 72px 16px;
  border: 1px dashed #d9dde7;
  border-radius: 6px;
  background: #fff;
  color: #8898aa;
  text-align: center;
}

@media (max-width: 767px) {
  .popup-toolbar-actions {
    width: 100%;
    margin-left: 0;
  }

  .popup-toolbar-actions .btn {
    flex: 1;
  }

  .popup-artist-filter {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }

  .popup-card-content {
    min-height: 300px;
  }
}
</style>
